import moment from 'moment';
import compact from 'lodash.compact';

const defaultFormat = /(\d{1,4})(\d{1,4})?(\d{1,4})?(\d{1,4})?(\d{1,3})?/;

const creditCards = [
  {
    type: 'visaelectron',
    patterns: [4026, 417500, 4405, 4508, 4844, 4913, 4917],
    format: defaultFormat,
    length: [16],
    cvcLength: [3],
    luhn: true,
  },
  {
    type: 'maestro',
    patterns: [5018, 502, 503, 56, 58, 639, 6220, 67],
    format: defaultFormat,
    length: [12, 13, 14, 15, 16, 17, 18, 19],
    cvcLength: [3],
    luhn: true,
  },
  {
    type: 'forbrugsforeningen',
    patterns: [600],
    format: defaultFormat,
    length: [16],
    cvcLength: [3],
    luhn: true,
  },
  {
    type: 'dankort',
    patterns: [5019],
    format: defaultFormat,
    length: [16],
    cvcLength: [3],
    luhn: true,
  },
  {
    type: 'visa',
    patterns: [4],
    format: defaultFormat,
    length: [16], //[13,16] officially
    cvcLength: [3],
    luhn: true,
  },
  {
    type: 'mastercard',
    patterns: [51, 52, 53, 54, 55, 22, 23, 24, 25, 26, 27],
    format: defaultFormat,
    length: [16],
    cvcLength: [3],
    luhn: true,
  },
  {
    type: 'amex',
    patterns: [34, 37],
    format: /(\d{1,4})(\d{1,6})?(\d{1,5})?/,
    length: [15],
    cvcLength: [3, 4],
    luhn: true,
  },
  {
    type: 'dinersclub',
    patterns: [30, 36, 38, 39],
    format: /(\d{1,4})(\d{1,6})?(\d{1,4})?/,
    length: [14],
    cvcLength: [3],
    luhn: true,
  },
  {
    type: 'discover',
    patterns: [60, 64, 65, 622],
    format: defaultFormat,
    length: [16],
    cvcLength: [3],
    luhn: true,
  },
  {
    type: 'unionpay',
    patterns: [62, 88],
    format: defaultFormat,
    length: [16, 17, 18, 19],
    cvcLength: [3],
    luhn: false,
  },
  {
    type: 'jcb',
    patterns: [35],
    format: defaultFormat,
    length: [16],
    cvcLength: [3],
    luhn: true,
  },
];

const validCardTypes = ['mastercard', 'visa', 'amex', 'discover'];

const patternMatch = (number, pattern, card) => number.startsWith(pattern);

export const retrieveCard = number =>
  creditCards.find(
    card =>
      card.patterns.some(pattern => patternMatch(number, pattern, card)) && card
  );

export default function determineCardType(number) {
  const card = retrieveCard(number);

  return (card && card.type) || 'none';
}

export const formatCardNumber = (number = '') => {
  const card = retrieveCard(number);

  const re = (card && card.format) || defaultFormat;
  const matches = re.exec(number.replace(/\D+/g, ''));

  if (!matches) {
    return number;
  }

  const [, ...creditSections] = matches;

  return compact(creditSections).join(' ');
};

const passingValidation = { passed: true, message: '' };

const validateExp = (exp, paymentDetails) => {
  const matches = exp.replace(/\D+/g, '').match(/(\d{1,2})(\d{4})?/);

  if (!matches) {
    return { passed: false, message: 'Please enter a valid expiration date.' };
  }

  const [, month, year] = matches;

  const expMoment = moment([parseInt(year, 10), parseInt(month, 10) - 1]);

  if (!month || !year || !expMoment.isValid()) {
    return { passed: false, message: paymentDetails.expirationValidation.date };
  } else if (expMoment.isBefore(moment(), 'month')) {
    return {
      passed: false,
      message: paymentDetails.expirationValidation.expired,
    };
  } else {
    return passingValidation;
  }
};

export const validateCard = ({ number, cvc, exp, paymentDetails }) => {
  let validations = {
    cvc: passingValidation,
    exp: validateExp(exp, paymentDetails),
    number: passingValidation,
  };

  const card = retrieveCard(number);

  if (!card) {
    validations.number = {
      passed: false,
      message: paymentDetails.cardValidation,
    };
  } else {
    const { cvcLength = [] } = card;

    if (!validCardTypes.some(type => type === card.type)) {
      validations.number = {
        passed: false,
        message: paymentDetails.typeValidation,
      };
    } else if (!cvcLength.some(l => l === cvc.length)) {
      validations.cvc = {
        passed: false,
        message: paymentDetails.cvcValidation,
      };
    }
  }

  return validations;
};
